import type * as React from "react";
import { memo, useState } from "react";
import Radio from "../../../../../common/components/Radio";
import DatePeriodPicker from "../../../../../common/components/DatePeriodPicker";
import { getPredefinedDatesDropdownOptions, PredefinedDate } from "../../../../common/utils/predefinedDates";
import dayjs from "dayjs";
import { Controller, type UseFormReturn } from "react-hook-form";
import { SimpleSelect } from "../../../../../common/components/Select/SimpleSelect";
import classNames from "classnames";
import classes from "./SearchPeriodSelector.module.less";
import { useTranslation } from "react-i18next";

interface SearchPeriodSelectorProps {
  onPeriodChange?: (dateFrom: Date | undefined, dateTo: Date | undefined, isSelectedPeriodWrong: boolean) => void;
  isDateValidForSelection: (date: Date) => boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formHooks: UseFormReturn<any>;
  disabled?: boolean;
  className?: string;
}

interface DatePickerData {
  start?: Date;
  end?: Date;
}

const SearchPeriodSelector: React.FC<SearchPeriodSelectorProps> = ({
  isDateValidForSelection,
  onPeriodChange,
  formHooks,
  disabled,
  className,
}) => {
  const [isSelectedPeriodWrong, setSelectedPeriodIsWrong] = useState(false);

  const { t } = useTranslation();
  const predefinedDates = getPredefinedDatesDropdownOptions(t);

  const setDatePeriod = (dateFrom?: Date, dateTo?: Date) => {
    const isSelectedPeriodWrong = !areDatesCorrect(dateFrom, dateTo);
    setSelectedPeriodIsWrong(isSelectedPeriodWrong);
    if (onPeriodChange) {
      onPeriodChange(dateFrom, dateTo, isSelectedPeriodWrong);
    }
  };

  const propsForm = {
    shouldValidate: true,
    shouldDirty: true,
  };

  const maxYear: number = new Date().getFullYear();
  const minYear: number = maxYear - 5;

  return (
    <div className={classNames(classes.container, className)}>
      <div className={classNames(classes.optionItem, classes.selectPredefinedDate)}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <Radio
              active={value}
              disabled={disabled}
              onClick={() => {
                formHooks.setValue("isCustomDate", false, propsForm);
                onChange(true);
              }}
            />
          )}
          name={"isPredefinedDate"}
          control={formHooks.control}
          defaultValue={true}
        />
        <Controller
          render={({ field: { value, onChange } }) => (
            <SimpleSelect
              label={t("orderList.date")}
              value={value}
              values={predefinedDates}
              fullWidth={true}
              disabled={disabled}
              onChange={(data) => {
                formHooks.setValue("isPredefinedDate", true, propsForm);
                formHooks.setValue("isCustomDate", false, propsForm);
                onChange(data);
              }}
              data-test-id={"search-order-quote-period-date-predefined"}
            />
          )}
          name={"predefinedDate"}
          control={formHooks.control}
          defaultValue={PredefinedDate.LAST_30_DAYS}
        />
      </div>
      <div className={classes.or}>{t("searchPage.refinement.value.label.or")}</div>
      <div className={classNames(classes.optionItem, classes.datePeriodInput)}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <Radio
              active={value}
              disabled={disabled}
              onClick={() => {
                formHooks.setValue("isPredefinedDate", false, propsForm);
                onChange(true);
              }}
            />
          )}
          name={"isCustomDate"}
          control={formHooks.control}
          defaultValue={false}
        />
        <Controller
          render={({ field: { value, onChange } }) => (
            <DatePeriodPicker
              minYear={minYear}
              maxYear={maxYear}
              dateFrom={value.start}
              dateTo={value.end}
              className={"flex-1"}
              disabled={disabled}
              isInvalid={isSelectedPeriodWrong}
              onPeriodUpdate={setDatePeriod}
              isDateValidForSelection={isDateValidForSelection}
              onChange={(data: DatePickerData) => {
                formHooks.setValue("isCustomDate", true, propsForm);
                formHooks.setValue("isPredefinedDate", false, propsForm);
                const dates = {
                  start: data.start || "",
                  end: data.end || "",
                };
                onChange(dates);
              }}
            />
          )}
          name={"chosenDate"}
          control={formHooks.control}
          defaultValue={{ start: "", end: "" }}
        />
      </div>
    </div>
  );
};

export default memo(SearchPeriodSelector);

function areDatesCorrect(dateFrom?: Date, dateTo?: Date) {
  if (!dateFrom || !dateTo) {
    return true;
  }

  return dayjs(dateFrom).isSameOrBefore(dateTo, "day");
}
